export function parseCampusTransferDetails(details = "") {
  const text = String(details || "").trim();
  const parsed = {
    campusFrom: "",
    campusTo: "",
    classFrom: "",
    classTo: "",
    raw: text,
  };

  const campusMatch = text.match(
    /Transferred from (.+?) to (.+?)(?:\s+and|\s*$)/i
  );
  if (campusMatch) {
    parsed.campusFrom = campusMatch[1].trim();
    parsed.campusTo = campusMatch[2].trim();
  }

  const classMatch = text.match(/Moved from (.+?) to (.+?)$/i);
  if (classMatch) {
    parsed.classFrom = classMatch[1].trim();
    parsed.classTo = classMatch[2].trim();
  }

  return parsed;
}

export function formatTransferTypeLabel(value) {
  if (value === "campus") return "Campus";
  if (value === "inner") return "Inner";
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatCampusTransferDateTime(value) {
  if (!value) return { date: "", time: "" };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  return {
    date: d.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

export function campusFromLabel(item = {}) {
  const parsed = parseCampusTransferDetails(item.details);
  return (
    item.metaFromCampusName ||
    parsed.campusFrom ||
    item.metaPreviousCampusName ||
    item.previousCampusName ||
    item.fromCampus ||
    ""
  );
}

export function campusToLabel(item = {}) {
  const parsed = parseCampusTransferDetails(item.details);
  return (
    item.metaToCampusName ||
    parsed.campusTo ||
    item.metaCampusName ||
    item.campusName ||
    item.toCampus ||
    ""
  );
}

export function staffCodeLabel(item = {}) {
  return (
    item.empCode ||
    item.employeeCode ||
    item.staffCode ||
    item.code ||
    item.grNo ||
    item.rfID ||
    item.staffID ||
    ""
  );
}

export function staffDepartmentLabel(item = {}) {
  return (
    item.departmentName ||
    item.meta_class ||
    item.metaDepartmentName ||
    item.department ||
    ""
  );
}

export function buildCampusTransferExcelRow(item, index) {
  const parsed = parseCampusTransferDetails(item.details);
  const classFallback =
    item.meta_class ||
    [item.className, item.sectionName].filter(Boolean).join(" - ");
  const updated = formatCampusTransferDateTime(item.updatedOn);

  return {
    "#": index + 1,
    "GR No": item.grNo || "",
    "Student Name": item.name || "",
    "Transfer Type": formatTransferTypeLabel(item.transferType),
    "From Class / Sec": parsed.classFrom || "",
    "To Class / Sec": parsed.classTo || classFallback || "",
    Details: item.details || "",
    "Updated Date": updated.date,
    "Updated Time": updated.time,
    "Updated By": item.metaUpdatedBy || "",
  };
}

function appendStaffTransferCampusParams(url, { campusID, campusIDs } = {}) {
  const ids = Array.isArray(campusIDs)
    ? campusIDs.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
    : String(campusIDs || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));

  // API: campusID=1 or campusIDs=0/1 => all campuses
  const isAll =
    !ids.length &&
    (campusID === undefined ||
      campusID === null ||
      campusID === "" ||
      Number(campusID) === 0 ||
      Number(campusID) === 1);

  if (ids.length > 1) {
    const filtered = ids.filter((id) => id !== 0);
    if (!filtered.length || filtered.every((id) => id === 1)) {
      return `${url}&campusID=1`;
    }
    return `${url}&campusIDs=${filtered.join(",")}`;
  }

  if (ids.length === 1) {
    const only = ids[0];
    if (only === 0 || only === 1) return `${url}&campusID=1`;
    return `${url}&campusID=${only}`;
  }

  if (isAll) return `${url}&campusID=1`;
  return `${url}&campusID=${Number(campusID)}`;
}

export function buildStaffTransferReportUrl({
  domain,
  db,
  campusID,
  campusIDs,
  dtFrom,
  dtTo,
} = {}) {
  let url = `${domain}Staff/StaffTransferReport?db=${db || ""}`;
  url = appendStaffTransferCampusParams(url, { campusID, campusIDs });
  if (dtFrom) url += `&dtFrom=${dtFrom}`;
  if (dtTo) url += `&dtTo=${dtTo}`;
  return url;
}

export function buildStaffTransferPaginationUrl({
  domain,
  db,
  campusID,
  campusIDs,
  page = 1,
  pageSize = 20,
  dtFrom,
  dtTo,
} = {}) {
  let url = `${domain}Staff/StaffTransferGetByPagination?db=${db || ""}`;
  url = appendStaffTransferCampusParams(url, { campusID, campusIDs });
  url += `&page=${page || 1}&pageSize=${pageSize || 20}`;
  if (dtFrom) url += `&dtFrom=${dtFrom}`;
  if (dtTo) url += `&dtTo=${dtTo}`;
  return url;
}

/** Manual entries — StaffTransferRecord/GetByPagination */
export function buildStaffTransferRecordPaginationUrl({
  domain,
  db,
  campusID,
  campusIDs,
  page = 1,
  pageSize = 20,
  staffID = 0,
  search,
  dtFrom,
  dtTo,
} = {}) {
  let url = `${domain}StaffTransferRecord/GetByPagination?db=${db || ""}`;
  url = appendStaffTransferCampusParams(url, { campusID, campusIDs });
  url += `&page=${page || 1}&pageSize=${pageSize || 20}`;
  const sid = Number(staffID);
  if (sid > 0) url += `&staffID=${sid}`;
  const q = String(search || "").trim();
  if (q) url += `&search=${encodeURIComponent(q)}`;
  if (dtFrom) url += `&dtFrom=${dtFrom}`;
  if (dtTo) url += `&dtTo=${dtTo}`;
  return url;
}

export function buildStaffCampusTransferExcelRow(item, index) {
  const transferred = formatCampusTransferDateTime(item.transferDate);

  return {
    "#": index + 1,
    "Staff ID": item.staffID || "",
    "Emp Code": staffCodeLabel(item),
    "Staff Name": item.name || "",
    "Father Name": item.fatherName || "",
    Designation: item.designation || "",
    Department: staffDepartmentLabel(item),
    Contact: item.contactNumber || "",
    "From Campus": campusFromLabel(item),
    "To Campus": campusToLabel(item),
    "Transfer Date": transferred.date,
    "Transfer Time": transferred.time,
    Notes: item.transferNotes || "",
    Details: item.details || "",
    "Transferred By": item.metaTransferredBy || "",
  };
}
